import React, { useCallback, memo } from 'react';
import { List } from 'react-window';

// Componente de fila para clientes (react-window v2 API)
const CustomerRow = memo(({ index, style, items, onSelect }) => {
  const customer = items?.[index];
  
  if (!customer) return null;
  
  const customerName = Array.isArray(customer.name) ? customer.name.join(' ') : (customer.name || 'Sin nombre');
  
  const thirdPartyTypeLabels = {
    'Customer': { label: 'Cliente', color: 'bg-blue-100 text-blue-700' },
    'Supplier': { label: 'Proveedor', color: 'bg-orange-100 text-orange-700' },
    'Other': { label: 'Otro', color: 'bg-gray-100 text-gray-700' },
    'Distributor': { label: 'Distribuidor', color: 'bg-green-100 text-green-700' },
    'Prospect': { label: 'Prospecto', color: 'bg-yellow-100 text-yellow-700' }
  };
  const typeInfo = thirdPartyTypeLabels[customer.third_party_type] || { label: 'Tercero', color: 'bg-gray-100 text-gray-700' };
  
  return (
    <div
      style={style}
      onClick={() => onSelect(customer, customerName)}
      className="p-3 hover:bg-purple-100 cursor-pointer border-b border-gray-200 transition-colors box-border"
      data-testid={`customer-option-${customer.identification}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-bold text-purple-700 text-base truncate flex-1">{customerName}</div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ml-2 ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
      </div>
      <div className="text-sm text-gray-600 mt-1 truncate">
        <span className="font-medium">ID:</span> {customer.identification}
        {customer.contacts && customer.contacts[0] && customer.contacts[0].email && (
          <span className="ml-3">
            <span className="font-medium">Email:</span> {customer.contacts[0].email}
          </span>
        )}
      </div>
    </div>
  );
});

// Componente de fila para productos (react-window v2 API)
const ProductRow = memo(({ index, style, items, onSelect }) => {
  const product = items?.[index];
  
  if (!product) return null;
  
  return (
    <div
      style={style}
      onClick={() => onSelect(product)}
      className="p-3 hover:bg-purple-100 cursor-pointer border-b border-gray-200 box-border"
      data-testid={`product-option-${product.codigo}`}
    >
      <div className="font-semibold text-purple-700">{product.codigo}</div>
      <div className="text-sm text-gray-600 truncate">{product.nombre}</div>
    </div>
  );
});

// Dropdown virtualizado para clientes
export const VirtualizedCustomerDropdown = memo(({ 
  customers, 
  onSelect, 
  maxHeight = 320,
  itemHeight = 72
}) => {
  const handleSelect = useCallback((customer, customerName) => {
    onSelect(customer, customerName);
  }, [onSelect]);
  
  // Validación defensiva
  if (!customers || !Array.isArray(customers) || customers.length === 0) {
    return null;
  }
  
  const listHeight = Math.min(maxHeight, customers.length * itemHeight);
  
  return (
    <div 
      className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden"
      data-testid="customer-dropdown"
    >
      <List
        height={listHeight}
        rowCount={customers.length}
        rowHeight={itemHeight}
        width="100%"
        rowComponent={CustomerRow}
        rowProps={{
          items: customers,
          onSelect: handleSelect
        }}
      />
    </div>
  );
});

// Dropdown virtualizado para productos
export const VirtualizedProductDropdown = memo(({ 
  products, 
  onSelect, 
  maxHeight = 240,
  itemHeight = 56
}) => {
  const handleSelect = useCallback((product) => {
    onSelect(product);
  }, [onSelect]);
  
  // Validación defensiva
  if (!products || !Array.isArray(products) || products.length === 0) {
    return null;
  }
  
  const listHeight = Math.min(maxHeight, products.length * itemHeight);
  
  return (
    <div 
      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
      data-testid="product-dropdown"
    >
      <List
        height={listHeight}
        rowCount={products.length}
        rowHeight={itemHeight}
        width="100%"
        rowComponent={ProductRow}
        rowProps={{
          items: products,
          onSelect: handleSelect
        }}
      />
    </div>
  );
});

export default { VirtualizedCustomerDropdown, VirtualizedProductDropdown };
